import React, { useState } from 'react';
import studiesData from '../data/studies.json';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

interface StudiesData {
  studies: Study[];
}

const { studies } = studiesData as StudiesData;

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  font-family: 'Satoshi', sans-serif;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const StudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const StudyCard = styled.div<{ isExpanded: boolean }>`
  background: #1a1a2e;
  border: 1px solid #2a2a4a;
  border-radius: 0;
  transition: all 0.3s ease;
  cursor: pointer;
  animation: ${fadeIn} 0.5s ease-out;
  
  ${({ isExpanded }) => isExpanded && `
    grid-column: 1 / -1;
    transform: scale(1.01);
    border-color: #4a4a6a;
  `}

  &:hover {
    transform: translateY(-2px);
    border-color: #4a4a6a;
    background: #202040;
  }
`;

const CardHeader = styled.div<{ isRct: boolean }>`
  padding: 1.5rem;
  border-bottom: 1px solid #2a2a4a;
  background: ${({ isRct }) => isRct ? '#252547' : '#1a1a2e'};
  color: #fff;
`;

const CardContent = styled.div`
  padding: 1.5rem;
  transition: all 0.3s ease;
`;

const Badge = styled.span`
  background: transparent;
  color: #fff;
  border: 1px solid #4a4a6a;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  margin-right: 0.5rem;
  font-family: 'Satoshi', sans-serif;
`;

interface Study {
  authors: string;
  journal: string;
  population: string;
  module: string;
  summary: string[]; // Array of summary points
  fullText: string; // Full text of the study details.
  isRct: boolean;
}

const StudyExplorer: React.FC = () => {
  const [expandedStudy, setExpandedStudy] = useState<number | null>(null);

  const toggleStudy = (index: number) => {
    setExpandedStudy(expandedStudy === index ? null : index);
  };

        return (
    <Container>
      <Header>
        <h2 style={{fontSize: '2.25rem', fontWeight: 700, marginBottom: '1rem', color: '#fff', fontFamily: 'Satoshi, sans-serif'}}>The Science Behind GGtude</h2>
        <p style={{color: '#ccc', maxWidth: '32rem', margin: '0 auto', fontFamily: 'Satoshi, sans-serif'}}>
          Explore our research portfolio of {studies.length} peer-reviewed studies, 
          including {studies.filter(s => s.isRct).length} randomized controlled trials
        </p>
      </Header>

      <StudyGrid>
{studies.map((study, index) => (
          <StudyCard 
            key={index}
            isExpanded={expandedStudy === index}
onClick={() => toggleStudy(index)}
>
            <CardHeader isRct={study.isRct}>
              <div className="flex items-center mb-2">
                <Badge>{study.module}</Badge>
                {study.isRct && <Badge>RCT</Badge>}
</div>
              <h3 style={{fontWeight: 'bold', fontSize: '1.125rem', marginBottom: '0.25rem', color: '#fff', fontFamily: 'Satoshi, sans-serif'}}>{study.authors}</h3>
              <p style={{fontSize: '0.875rem', color: '#ccc', fontFamily: 'Satoshi, sans-serif'}}>{study.journal}</p>
              <p style={{fontSize: '0.875rem', color: '#ccc', fontFamily: 'Satoshi, sans-serif'}}>Population: {study.population}</p>
            </CardHeader>

            <CardContent>
{expandedStudy === index && (
                <>
                  <ul style={{listStyle: 'disc', marginLeft: '1.25rem', marginBottom: '1rem', color: '#ccc', fontFamily: 'Satoshi, sans-serif'}}>
                    {study.summary.map((point, i) => (
                      <li key={i} style={{marginBottom: '0.5rem'}}>{point}</li>
                    ))}
                  </ul>
<p style={{color: '#ccc', whiteSpace: 'pre-line', fontFamily: 'Satoshi, sans-serif'}}>{study.fullText}</p>
                </>
)}
            </CardContent>
          </StudyCard>
))}
      </StudyGrid>
    </Container>
);
};

export default StudyExplorer;